import React, { Component } from "react";
import { Input, Select, Checkbox, DatePicker, Radio, Form } from "antd";
import { Link } from "react-router-dom";
import moment from "moment";

const { Option } = Select;

/**
 * DeliveryDetails
 *
 * Reusable presentational block for the ANC "Delivery Details" section.
 * Renders Form.Item fields, so it MUST be rendered inside an antd <Form>.
 *
 * Layout:
 * - Common (whole-delivery) fields shown once: Year (current, disabled),
 *   Gestational Age (disabled), Mode of Conception, Mode of Delivery,
 *   Place of Delivery, Outcome, and LSCS Caesarean type + Indication.
 * - Per-fetus birth details repeated once per fetus (driven by `fetuses`):
 *   Birth Date & Time, Gender, Birth Weight, Birth Height, Head Circumference,
 *   and Congenital Abnormality (+ details). These are saved to multiple_pregnancies.
 * - When Outcome is Live Term / Live Pre-Term, the per-fetus birth fields are mandatory.
 *
 * Props:
 * - allowEdit (bool)
 * - values: common field values (pregnancy_year, delivery_gestational_age, delivery_mode,
 *   delivery_type, delivery_place, delivery_outcome, caesarean_section_type,
 *   delivery_indication_for_c_section, delivery_details)
 * - fetuses: array of per-fetus objects
 * - deliveryOptions, placeOptions, genderOptions (arrays)
 * - speech: { identifier, state }
 * Handlers:
 * - onSelectChangeList(value, props, field)  // common selects
 * - onTextChange(event)                      // common textareas
 * - onCaesareanTypeChange(event)
 * - onFetusField(index, field, value)        // per-fetus field update
 * - onFetusCongenitalChange(index, checked)
 * - initSpeech, autoHeight, validateNumber, normalizeValue, disabledPastDate
 */
class DeliveryDetails extends Component {
  render() {
    const {
      allowEdit,
      values = {},
      fetuses = [],
      deliveryOptions = [],
      outcomeOptions = [],
      placeOptions = [],
      genderOptions = [],
      speech = {},
      onSelectChangeList,
      onTextChange,
      onCaesareanTypeChange,
      onFetusField,
      onFetusCongenitalChange,
      initSpeech,
      autoHeight,
      validateNumber,
      normalizeValue,
      disabledPastDate,
    } = this.props;

    const isLscs = values.delivery_type == "2";
    // Per-fetus birth details are no longer mandatory for any outcome.
    const birthMandatory = false;
    // Spontaneous Abortion (5) / MTP (6): no fetus/birth data — hide the per-fetus block.
    const noFetusOutcome = String(values.delivery_outcome) === "5" || String(values.delivery_outcome) === "6";
    // Everything below Outcome is revealed only once an Outcome is selected. Until then only
    // Year / Gestational Age / Outcome are shown.
    const outcomeSelected = values.delivery_outcome != null && values.delivery_outcome !== "";

    return (
      <>
        <div className='row mt-4 '>
          <div className='col-12'>
            <span className='title'>Delivery Details</span>
          </div>
        </div>
        {/* Sequence: Year, Gestational Age (GA), then Outcome. The remaining fields appear
            only after an Outcome is chosen. */}
        <div className='row mt-3'>
          <div className='col-4'>
            <div className="antInputItem hasdata">
              <Form.Item label="Year" name="pregnancy_year">
                <Input name="pregnancy_year" disabled value={values.pregnancy_year} />
              </Form.Item>
            </div>
          </div>
          <div className='col-4'>
            <div className="antInputItem hasdata">
              <Form.Item label="Gestational Age" name="delivery_gestational_age">
                <Input name="delivery_gestational_age" disabled value={values.delivery_gestational_age} />
              </Form.Item>
            </div>
          </div>
          <div className='col-4'>
            <div className="antSelectItem hasdata">
              <Form.Item label="Outcome" name="delivery_outcome"
                rules={[{ required: true, message: "Select Outcome" }]}
              >
                <Select
                  style={{ width: '100%' }}
                  name="delivery_outcome"
                  disabled={!allowEdit}
                  value={values.delivery_outcome}
                  onChange={(val, props) => onSelectChangeList(val, props, 'delivery_outcome')}
                >
                  {outcomeOptions.map((d, i) => (
                    <Option key={i} value={d.val} label={d.label}>{d.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>
        </div>

        {
          /* Mode of Conception, Mode of Delivery and Place of Delivery are shown only after an
             Outcome has been selected; with no Outcome, none of the fields below are shown. */
          outcomeSelected
          ?
        <>
        <div className='row mt-3'>
          {/* Mode of Conception is hidden for Spontaneous Abortion / MTP (no conception context). */}
          {!noFetusOutcome && (
          <div className='col-4'>
            <div className="antSelectItem hasdata">
              <Form.Item label="Mode of Conception" name="delivery_mode">
                <Select
                  style={{ width: '100%' }}
                  name="delivery_mode"
                  disabled={!allowEdit}
                  value={values.delivery_mode}
                  onChange={(val, props) => onSelectChangeList(val, props, 'delivery_mode')}
                >
                  <Option value="1">Natural</Option>
                  <Option value="2">IVF</Option>
                  <Option value="3">IUI</Option>
                </Select>
              </Form.Item>
            </div>
          </div>
          )}
          {/* Mode of Delivery and Place of Delivery are hidden for Spontaneous Abortion / MTP. */}
          {!noFetusOutcome && (
          <div className="col-4">
            <div className="antSelectItem hasdata">
              <Form.Item label="Mode of Delivery" name="delivery_type">
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  name="delivery_type"
                  id="delivery_type"
                  disabled={!allowEdit}
                  value={values.delivery_type}
                  optionFilterProp="children"
                  filterOption={(input, option) => (option.children.toString().toLowerCase().indexOf(input.toString().toLowerCase()) >= 0)}
                  onChange={(val, props) => onSelectChangeList(val, props, 'delivery_type')}
                >
                  {deliveryOptions.map((d, i) => (
                    <Option key={i} value={d.val} label={d.label}>{d.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>
          )}
          {!noFetusOutcome && (
          <div className='col-4'>
            <div className="antSelectItem hasdata">
              <Form.Item label="Place of Delivery" name="delivery_place">
                <Select
                  style={{ width: '100%' }}
                  disabled={!allowEdit}
                  name="delivery_place"
                  value={values.delivery_place}
                  onChange={(val, props) => onSelectChangeList(val, props, 'delivery_place')}
                >
                  {placeOptions.map((d, i) => (
                    <Option key={i} value={d.val} label={d.label}>{d.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>
          )}
        </div>

        {
          isLscs
          ?
            <div className='row mt-3'>
              <div className='col-4'>
                {/* Plain (non-emrfrm) label + radios with inline styles so the global
                    floating-label CSS does not absolutely-position the label over the radios. */}
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'block', marginBottom: '8px', color: '#8c8c8c', fontSize: '13px', position: 'static' }}>
                    Caesarean Section
                  </div>
                  <Radio.Group
                    disabled={!allowEdit}
                    value={values.caesarean_section_type}
                    onChange={(e) => onCaesareanTypeChange(e)}
                  >
                    <Radio value="emergency" style={{ marginRight: '20px' }}>Emergency</Radio>
                    <Radio value="elective">Elective</Radio>
                  </Radio.Group>
                </div>
              </div>
              <div className='col-8'>
                <div className={`form-group antInputItem micwrap hasdata`}>
                  <Link
                    onClick={(e) => { e.preventDefault(); initSpeech({ identifier: "delivery_indication_for_c_section" }); }}
                    className={speech.identifier === "delivery_indication_for_c_section" && speech.state ? 'mic r-0 on mute' : 'mic r-0'}
                  ></Link>
                  <Form.Item name="delivery_indication_for_c_section" label="Indication" id="delivery_indication_for_c_section"
                    onInput={(e) => autoHeight(e)}
                  >
                    <textarea className="form-control diagnosis_notes_class speech-field" value={values.delivery_indication_for_c_section}
                      name="delivery_indication_for_c_section"
                      onChange={onTextChange}
                      disabled={!allowEdit}
                    />
                  </Form.Item>
                </div>
              </div>
            </div>
          :
          null
        }

        {/* Per-fetus birth details: one block per fetus. In edit mode, show all rows. In view
            mode, filter empty rows. Hidden entirely for Spontaneous Abortion / MTP outcomes. */}
        {!noFetusOutcome && fetuses.map((f, index) => {
          // In view mode (allowEdit=false), skip empty rows. In edit mode, show all rows.
          const hasData = f && (f.delivery_bith_date_time || f.delivery_gender || f.delivery_birth_weight || f.delivery_birth_height || f.delivery_birth_head_cc || f.delivery_congenital_abnormality);
          if (!allowEdit && !hasData) {
            return null;
          }
          return (
          <React.Fragment key={index}>
            {
              fetuses.length > 1
              ?
                <div className='row mt-3'>
                  <div className='col-12'>
                    <h6 className='text-primary mb-0'>{'Fetus ' + (index + 1)}</h6>
                  </div>
                </div>
              :
              null
            }
            <div className='row mt-3'>
              <div className='col-3'>
                <div className="antSelectItem hasdata">
                  <Form.Item
                    name={['fetus_birth', index, 'delivery_bith_date_time']}
                    className='mb-0'
                    label="Birth Date & Time"
                    rules={[{ required: birthMandatory, message: "Birth Date & Time is Required" }]}
                  >
                    <DatePicker showTime={{ use12Hours: true, format: 'hh:mm A' }}
                      format="DD MMM, YYYY hh:mm A"
                      disabledDate={disabledPastDate}
                      disabled={!allowEdit}
                      value={f.delivery_bith_date_time ? moment(f.delivery_bith_date_time, 'YYYY-MM-DD HH:mm:ss') : null}
                      placeholder=''
                      className='mb-0'
                      style={{ width: '100%', height: '41px' }}
                      onChange={(date) => onFetusField(index, 'delivery_bith_date_time', date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : null)}
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="col-3">
                <div className="antSelectItem hasdata">
                  <Form.Item label="Gender" name={['fetus_birth', index, 'delivery_gender']}
                    rules={[{ required: birthMandatory, message: "Select Gender" }]}
                  >
                    <Select
                      style={{ width: '100%' }}
                      showSearch
                      optionFilterProp="children"
                      disabled={!allowEdit}
                      value={f.delivery_gender}
                      filterOption={(input, option) => (option.children.toString().toLowerCase().indexOf(input.toString().toLowerCase()) >= 0)}
                      onChange={(val) => onFetusField(index, 'delivery_gender', val)}
                    >
                      {genderOptions.map((d, i) => (
                        <Option key={i} value={d.val} label={d.label}>{d.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>
              <div className="col-3">
                <div className="antSelectItem hasdata">
                  <Form.Item name={['fetus_birth', index, 'delivery_birth_weight']} label="Birth Weight (kg)"
                    rules={[
                      { required: birthMandatory, message: "Birth Weight is Required" },
                      { validator: validateNumber, transform: normalizeValue }
                    ]}
                  >
                    <Input
                      type='number'
                      disabled={!allowEdit}
                      value={f.delivery_birth_weight}
                      onChange={(e) => onFetusField(index, 'delivery_birth_weight', e.target.value)}
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="col-3">
                <div className="antSelectItem hasdata">
                  <Form.Item name={['fetus_birth', index, 'delivery_birth_height']} label="Birth Height (cm)"
                    rules={[
                      { required: birthMandatory, message: "Birth Height is Required" },
                      { validator: validateNumber, transform: normalizeValue }
                    ]}
                  >
                    <Input
                      type='number'
                      disabled={!allowEdit}
                      value={f.delivery_birth_height}
                      onChange={(e) => onFetusField(index, 'delivery_birth_height', e.target.value)}
                    />
                  </Form.Item>
                </div>
              </div>
            </div>
            <div className='row mt-3'>
              <div className="col-3">
                <div className="antSelectItem hasdata">
                  <Form.Item name={['fetus_birth', index, 'delivery_birth_head_cc']} label="Head Circumference (cm)"
                    rules={[
                      { required: birthMandatory, message: "Head Circumference is Required" },
                      { validator: validateNumber, transform: normalizeValue }
                    ]}
                  >
                    <Input
                      type='number'
                      disabled={!allowEdit}
                      value={f.delivery_birth_head_cc}
                      onChange={(e) => onFetusField(index, 'delivery_birth_head_cc', e.target.value)}
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="col-3">
                <div className="form-group">
                  <div className="antSelectItem hasdata">
                    <Form.Item name={['fetus_birth', index, 'delivery_congenital_abnormality']} valuePropName="checked">
                      <Checkbox
                        disabled={!allowEdit}
                        checked={f.delivery_congenital_abnormality === true}
                        onChange={(e) => onFetusCongenitalChange(index, e.target.checked)}
                      >
                        Congenital Abnormality
                      </Checkbox>
                    </Form.Item>
                  </div>
                </div>
              </div>
              {
                f.delivery_congenital_abnormality == true
                ?
                  <div className="col-6">
                    <div className='antInputItem hasdata'>
                      <Form.Item
                        name={['fetus_birth', index, 'delivery_congenital_abnormality_remarks']}
                        label="Congenital Abnormality Details"
                        rules={[{ required: true, message: "Congenital Abnormality Details Required" }]}
                      >
                        <textarea className="form-control diagnosis_notes_class"
                          value={f.delivery_congenital_abnormality_remarks}
                          disabled={!allowEdit}
                          onChange={(e) => onFetusField(index, 'delivery_congenital_abnormality_remarks', e.target.value)}
                        />
                      </Form.Item>
                    </div>
                  </div>
                :
                null
              }
            </div>
          </React.Fragment>
          );
        })}

        <div className='row mt-3'>
          <div className='col-12'>
            <div className="form-group">
              <div className={`form-group antInputItem micwrap hasdata`}>
                <a
                  onClick={(e) => { e.preventDefault(); initSpeech({ identifier: "delivery_details" }); }}
                  className={speech.identifier === "delivery_details" && speech.state ? 'mic r-0 on mute' : 'mic r-0'}
                ></a>
                <Form.Item name="delivery_details" label="Details" id="delivery_details"
                  onInput={(e) => autoHeight(e)}
                >
                  <textarea className="form-control diagnosis_notes_class speech-field" value={values.delivery_details}
                    name="delivery_details"
                    onChange={onTextChange}
                    disabled={!allowEdit}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        </div>

        <div className='row mt-3'>
          <div className='col-6'>
            <div className={`form-group antInputItem micwrap hasdata`}>
              <a
                onClick={(e) => { e.preventDefault(); initSpeech({ identifier: "antenatal_details" }); }}
                className={speech.identifier === "antenatal_details" && speech.state ? 'mic r-0 on mute' : 'mic r-0'}
              ></a>
              <Form.Item name="antenatal_details" label="Antenatal Details" id="antenatal_details"
                onInput={(e) => autoHeight(e)}
              >
                <textarea className="form-control diagnosis_notes_class speech-field" value={values.antenatal_details}
                  name="antenatal_details"
                  onChange={onTextChange}
                  disabled={!allowEdit}
                />
              </Form.Item>
            </div>
          </div>
          <div className='col-6'>
            <div className={`form-group antInputItem micwrap hasdata`}>
              <a
                onClick={(e) => { e.preventDefault(); initSpeech({ identifier: "remarks" }); }}
                className={speech.identifier === "remarks" && speech.state ? 'mic r-0 on mute' : 'mic r-0'}
              ></a>
              <Form.Item name="remarks" label="Remarks" id="remarks"
                onInput={(e) => autoHeight(e)}
              >
                <textarea className="form-control diagnosis_notes_class speech-field" value={values.remarks}
                  name="remarks"
                  onChange={onTextChange}
                  disabled={!allowEdit}
                />
              </Form.Item>
            </div>
          </div>
        </div>
        </>
          :
          null
        }
      </>
    );
  }
}

export default DeliveryDetails;
